import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  CalendarClock,
  Mail,
  Megaphone,
  MessageSquare,
  Plus,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react'

import {
  CAMPAIGN_CHANNEL_LABELS,
  CAMPAIGN_STATUS_LABELS,
  CAMPAIGN_STATUS_VARIANT,
  CAMPAIGN_TRIGGER_LABELS,
  type Campaign,
} from '@/types/marketing'
import {
  useCampaignTemplates,
  useCampaigns,
  useCancelCampaign,
  useCreateCampaign,
  useDeleteCampaign,
  useScheduleCampaign,
  useSegmentPreview,
  useSegments,
  useSendCampaign,
} from '@/features/marketing/hooks'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export function CampaignsTab() {
  const { data: campaigns = [], isLoading } = useCampaigns()
  const [createOpen, setCreateOpen] = useState(false)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Email and SMS blasts to a segment, with a per-recipient send log.
        </p>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> New Campaign
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No campaigns yet — create a template and a segment, then send your first blast.
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Delivery</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead className="w-40 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((c) => (
                <CampaignRow key={c.id} campaign={c} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <CampaignCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  )
}

function CampaignRow({ campaign }: { campaign: Campaign }) {
  const sendMutation = useSendCampaign()
  const cancelMutation = useCancelCampaign()
  const deleteMutation = useDeleteCampaign()
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [scheduleWhen, setScheduleWhen] = useState('')
  const [deleteOpen, setDeleteOpen] = useState(false)

  const editable = campaign.status === 'draft' || campaign.status === 'scheduled'
  const pending = sendMutation.isPending || cancelMutation.isPending

  const openSchedule = () => {
    // Default: one hour from now, minute resolution. Computed in the event
    // handler — Date.now() is not allowed during render.
    const next = new Date(Date.now() + 60 * 60 * 1000)
    next.setSeconds(0, 0)
    setScheduleWhen(next.toISOString().slice(0, 16))
    setScheduleOpen(true)
  }

  return (
    <TableRow>
      <TableCell>
        <Link
          to="/marketing/$campaignId"
          params={{ campaignId: campaign.id }}
          className="font-medium hover:underline"
        >
          {campaign.name}
        </Link>
        <div className="text-xs text-muted-foreground">
          {CAMPAIGN_TRIGGER_LABELS[campaign.trigger_type]}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="gap-1">
          {campaign.channel === 'email' ? (
            <Mail className="h-3 w-3" />
          ) : (
            <MessageSquare className="h-3 w-3" />
          )}
          {CAMPAIGN_CHANNEL_LABELS[campaign.channel]}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge variant={CAMPAIGN_STATUS_VARIANT[campaign.status]}>
          {CAMPAIGN_STATUS_LABELS[campaign.status]}
        </Badge>
      </TableCell>
      <TableCell className="text-sm">
        {campaign.stats.recipients > 0 ? (
          <span>
            {campaign.stats.sent}/{campaign.stats.recipients} delivered
            {campaign.stats.skipped > 0 && (
              <span className="text-muted-foreground"> · {campaign.stats.skipped} skipped</span>
            )}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {campaign.sent_at ? new Date(campaign.sent_at).toLocaleDateString() : '—'}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          {editable && (
            <>
              <Button
                variant="ghost"
                size="icon"
                title="Send now"
                disabled={pending}
                onClick={() => sendMutation.mutate(campaign.id)}
              >
                <Send className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" title="Schedule" onClick={openSchedule}>
                <CalendarClock className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                title="Cancel"
                disabled={pending}
                onClick={() => cancelMutation.mutate(campaign.id)}
              >
                <XCircle className="h-4 w-4" />
              </Button>
            </>
          )}
          {campaign.status === 'draft' && (
            <Button variant="ghost" size="icon" title="Delete" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </TableCell>

      <ScheduleDialog
        campaign={campaign}
        when={scheduleWhen}
        onWhenChange={setScheduleWhen}
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
      />
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete campaign?</DialogTitle>
            <DialogDescription>
              “{campaign.name}” is removed. Only drafts can be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={async () => {
                await deleteMutation.mutateAsync(campaign.id)
                setDeleteOpen(false)
              }}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TableRow>
  )
}

function ScheduleDialog({
  campaign,
  when,
  onWhenChange,
  open,
  onOpenChange,
}: {
  campaign: Campaign
  when: string
  onWhenChange: (value: string) => void
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const scheduleMutation = useScheduleCampaign()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Schedule “{campaign.name}”</DialogTitle>
          <DialogDescription>
            The campaign sends automatically once the scheduled time arrives.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="text-sm font-medium">Send at</label>
          <Input
            type="datetime-local"
            value={when}
            onChange={(e) => onWhenChange(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!when || scheduleMutation.isPending}
            onClick={async () => {
              await scheduleMutation.mutateAsync({
                id: campaign.id,
                scheduled_at: new Date(when).toISOString(),
              })
              onOpenChange(false)
            }}
          >
            {scheduleMutation.isPending ? 'Scheduling…' : 'Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CampaignCreateDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { data: templates = [] } = useCampaignTemplates()
  const { data: segments = [] } = useSegments()
  const createMutation = useCreateCampaign()
  const sendMutation = useSendCampaign()
  const previewMutation = useSegmentPreview()

  const [name, setName] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [segmentId, setSegmentId] = useState('')

  // Reset the form whenever the dialog opens ("adjust state during render",
  // see React docs). The stale preview, if any, self-hides because segmentId
  // resets and the preview block renders only for a selected segment.
  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    if (open) {
      setName('')
      setTemplateId('')
      setSegmentId('')
    }
  }

  const segment = segments.find((s) => s.id === segmentId)

  const preview = previewMutation.data
  const pending = createMutation.isPending || sendMutation.isPending
  const canSubmit = name.trim().length > 0 && templateId && segmentId

  const submit = async (sendNow: boolean) => {
    if (!canSubmit) return
    try {
      const created = await createMutation.mutateAsync({
        name: name.trim(),
        template_id: templateId,
        segment_id: segmentId,
      })
      if (sendNow) await sendMutation.mutateAsync(created.id)
      onOpenChange(false)
    } catch {
      // error surfaces via the global mutation toast; keep dialog open
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5" /> New Campaign
          </DialogTitle>
          <DialogDescription>
            Pick a template and a segment — the audience evaluates at send time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Name</label>
            <Input
              placeholder="Winterization special — October"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Template</label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger>
                <SelectValue
                  placeholder={templates.length ? 'Select template' : 'No templates yet'}
                />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} · {CAMPAIGN_CHANNEL_LABELS[t.channel]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Segment</label>
            <Select value={segmentId} onValueChange={setSegmentId}>
              <SelectTrigger>
                <SelectValue placeholder={segments.length ? 'Select segment' : 'No segments yet'} />
              </SelectTrigger>
              <SelectContent>
                {segments.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {segment && (
            <div className="flex items-center justify-between rounded-md border bg-muted/40 px-4 py-3 text-sm">
              {preview ? (
                <span>
                  <span className="font-semibold">{preview.count} customers</span>
                  {preview.skipped > 0 && (
                    <span className="text-muted-foreground"> · {preview.skipped} unreachable</span>
                  )}
                </span>
              ) : (
                <span className="text-muted-foreground">Check the segment's live audience.</span>
              )}
              <Button
                variant="outline"
                size="sm"
                disabled={previewMutation.isPending}
                onClick={() => previewMutation.mutate(segment.rules)}
              >
                {previewMutation.isPending ? 'Checking…' : 'Preview'}
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button variant="outline" onClick={() => submit(false)} disabled={pending || !canSubmit}>
            Save Draft
          </Button>
          <Button onClick={() => submit(true)} disabled={pending || !canSubmit}>
            <Send className="mr-2 h-4 w-4" />
            {sendMutation.isPending ? 'Sending…' : 'Create & Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
